// IsomorphicService per ADR 010 — pure, read-only.
// THE buyer's-premium summary formatter (BL-983).
//
// Replaces four divergent formatters that rendered the same stored config
// differently — `bpSummary` (admin inheritance pills, which showed "Custom" for
// anything it didn't recognise), `formatBpTerms` (invoice details + the printed
// Buyers Guide), the tenant-settings read card, and bidder-web's
// `formatBuyersPremium` (which flattened every tiered schedule to the useless
// "Buyer's premium applies"). Items 5-7 migrate the screens onto this one.
//
// The output strings are fixed by the approved v2 mockups — see
// `__tests__/format.test.ts`, which records which strings are verbatim from the
// mockup and which are extrapolated. ONE deliberate supersession since: BL-1375
// renamed the single-rate type label from "Flat" to "Standard" (see
// BUYERS_PREMIUM_TYPE_LABELS below). Nothing else the mockups pinned moved.
//
// Deliberately NOT a rate calculator: it never blends tiers into an "effective
// rate". The mockups call that out explicitly — a blended "16.4% on this bid" is
// a number that appears on no document the buyer ever receives.

import Decimal from 'decimal.js';

import { formatScheduleAmount, type ScheduleCurrencyOption } from '../money/display-text';
import { format } from '../money/format';
import { Money } from '../money/money';
import { Rate } from '../rate/rate';
import type { PremiumSchedule } from './schedule';
import {
  activeBound,
  isUsableBuyersPremium,
  type BuyersPremiumCombineMode,
  type BuyersPremiumLike,
} from './types';
import { isPremiumSchedule } from './wire';

/**
 * What every formatter here accepts: an exact {@link PremiumSchedule} (what a
 * client decodes off the wire, BL-2482) or the stored number shape. Both
 * normalize to one display form, so the same schedule prints the same text
 * whichever way it arrived. The stored arm stays while the ring surfaces
 * (BL-2490) and bidder-web (BL-2483) still pass it.
 */
export type BuyersPremiumScheduleInput = PremiumSchedule | BuyersPremiumLike;

/**
 * An amount as it arrived: a `Money` (a decoded PremiumSchedule, which prints in
 * its own currency) or a stored JSONB number (which prints in the caller's).
 */
type DisplayAmount = Money | number;

/** One band, normalized: an exact rate, and its upper bound as it arrived. */
interface DisplayTier {
  rate: Rate;
  threshold: DisplayAmount | null;
}

/** A usable schedule normalized for display. Bounds are active-only (`null` = none). */
type DisplaySchedule =
  | { type: 'Flat'; rate: Rate; minFee: DisplayAmount | null; maxFee: DisplayAmount | null }
  | {
      type: 'Tiered';
      combineMode: BuyersPremiumCombineMode;
      tiers: DisplayTier[];
      minFee: DisplayAmount | null;
      maxFee: DisplayAmount | null;
    };

/**
 * Prints one schedule amount. A Money prints in its own currency through the
 * shared formatter; a stored number in the caller's ({@link formatScheduleAmount},
 * which prints a bare grouped number for an unknown currency, BUG-1909). Both
 * use `when-whole`, so the same value prints the same either way.
 */
function amountText(amount: DisplayAmount, currency: string | null | undefined): string {
  return amount instanceof Money ? format(amount, { wholeUnits: 'when-whole' }) : formatScheduleAmount(amount, currency);
}

/** An amount's exact value, for ordering and for the representation-free disclosure key. */
function amountValue(amount: DisplayAmount): Decimal {
  return amount instanceof Money ? amount.toDecimal() : new Decimal(amount);
}

/**
 * Normalizes either input to the display form, or `null` when there is no
 * usable schedule (absent, tier-less, rate-less).
 */
function toDisplay(bp: BuyersPremiumScheduleInput): DisplaySchedule | null {
  if (isPremiumSchedule(bp)) {
    const bounds = { minFee: bp.minFee, maxFee: bp.maxFee };
    if (bp.type === 'Flat') return { type: 'Flat', rate: bp.rate, ...bounds };
    return { type: 'Tiered', combineMode: bp.combineMode, tiers: bp.tiers, ...bounds };
  }
  if (!isUsableBuyersPremium(bp)) return null;
  // A stored bound is active only when positive and finite ({@link activeBound}).
  const bounds = { minFee: activeBound(bp.minFee), maxFee: activeBound(bp.maxFee) };
  if (bp.type === 'Flat') return { type: 'Flat', rate: Rate.fromPercent(bp.flatRate), ...bounds };
  return {
    type: 'Tiered',
    combineMode: bp.combineMode ?? 'Marginal',
    tiers: bp.tiers.map((t) => ({
      rate: Rate.fromPercent(t.rate),
      threshold: t.threshold == null || !Number.isFinite(t.threshold) ? null : t.threshold,
    })),
    ...bounds,
  };
}

/** Options controlling how much of the summary is rendered. */
export interface FormatBuyersPremiumOptions extends ScheduleCurrencyOption {
  /**
   * Drop the leading type label (`Standard · ` / `Tiered (Marginal) · `), keeping
   * just the rates and bounds. For space-constrained surfaces where the context
   * already says "buyer's premium" — the Sequencing ring chips (item 6).
   */
  compact?: boolean;
}

/**
 * Print a stored buyer's-premium percent as the rate it bills at: `"16.5%"`,
 * `"10%"`, `"16.6667%"` for a stored `16.66666`.
 *
 * THE one percent label for a premium (BUG-2051). Every schedule prices through
 * `Rate.fromPercent` (`premiumScheduleFromStored`), which rounds to Rate's
 * six-place fraction; a surface that printed the stored number itself (or
 * `toFixed(2)` of it) told the buyer a different rate from the one billed. Any
 * surface naming a premium percent goes through here, never `${rate}%`.
 *
 * @param percent - The stored percent (`18` for 18%).
 * @returns The Rate's display string, trailing zeros trimmed.
 * @sideEffect None — pure.
 */
export function formatBuyersPremiumRate(percent: number): string {
  return Rate.fromPercent(percent).toPercent();
}

/** The separator between every segment of the summary. */
const SEP = ' · ';

/**
 * Beyond this many tiers the summary shows only the first band plus a
 * "+N tiers" count, and the surface is expected to offer the full breakdown on
 * hover (mockup §4). Two-tier schedules — by far the most common — render whole.
 */
const MAX_TIERS_SHOWN = 2;

/**
 * The copy every surface uses for a schedule that charges the buyer nothing.
 *
 * EXPORTED because four surfaces render this exact sentence and, until BUG-1031,
 * each hand-wrote it — two of them with a curly apostrophe and two with a
 * straight one, so the same label read differently on admin and on the bidder's
 * ring table. Nobody chose that; it was drift, and a private constant is what
 * allowed it. Import this rather than retyping the string.
 *
 * The apostrophe is U+2019 (’), the typographically correct one, matching the
 * bidder's neighbouring copy ("Buyer’s Premium", "10% buyer’s premium").
 *
 * Callers that render it as an UNSET fallback (rather than as this module's
 * waiver answer) are legitimate and expected — `formatBuyersPremium` returns ''
 * for unset precisely so the caller owns that decision. What they must not do is
 * re-type the sentence.
 */
export const NO_PREMIUM = 'No buyer\u2019s premium';

/**
 * Whether a NORMALIZED schedule genuinely charges the buyer nothing.
 *
 * BOTH calculation types can express a waiver, and this predicate treats them
 * alike: a 0% flat rate, and a tiered schedule whose EVERY band is 0%. The
 * second is an unusual way for an operator to waive a premium, but the admin
 * editor explicitly permits a tier rate of 0, so the shape is reachable — and a
 * buyer under it pays exactly the same nothing. Describing one as a waiver and
 * the other as a tiered premium with a list of 0% bands told the same fact two
 * ways and made the buyer decode the second (BUG-1031).
 *
 * The floor is checked FIRST because it rescues either branch. A `minFee` is
 * applied AFTER the rate (`applyFeeCap` raises the premium to the floor whenever
 * `hammerPrice > 0`), so `{flatRate: 0, minFee: 100}` — and equally an all-zero
 * tiered schedule carrying that floor — bills $100 on every non-free lot.
 * Reporting either as "No buyer's premium" would tell the buyer their total
 * equals their bid when it does not, the exact mis-disclosure this module exists
 * to prevent. A `maxFee` is irrelevant to both: capping zero still yields zero.
 *
 * The tiered arm asks whether EVERY rate is zero, never just the first: a
 * `[0% to $5,000, 16% above]` schedule bills 16% past the threshold and must
 * keep its full band list. `settings.tiers` is non-empty by the time this runs:
 * `toDisplay` returns `null` for anything else.
 */
function chargesNothing(settings: DisplaySchedule): boolean {
  // A live floor bills regardless of the rate, so it settles the question for
  // both branches before either rate is examined.
  if (settings.minFee != null) return false;

  return settings.type === 'Flat'
    ? settings.rate.isZero()
    : settings.tiers.every((tier) => tier.rate.isZero());
}

/**
 * The operator-facing name of each calculation type.
 *
 * THE single source for this word (BL-1375). It had five: this module's leading
 * prefix ("Flat"), the editor's radio literal ("Single Rate (%)"), the settings
 * summary row echoing the stored enum ("Flat"), the editor preview's own label
 * ("10% flat"), and the bidder ring table's " flat" suffix. BL-1140 renamed one
 * of the five and the other four silently disagreed with it for eleven days.
 *
 * `Flat` is the PERSISTED value and does not move — this maps it to what an
 * operator reads. "Standard" is the word the seller-commission editor already
 * shipped (BL-1111), so the two sides of the house name this concept alike.
 *
 * Consumers: {@link formatBuyersPremium}'s leading segment,
 * {@link buyersPremiumTypeLabel} (the settings summary row + the editor preview),
 * and the editor's Calculation Type radio. Anything naming the type reads from
 * here — never a literal.
 */
export const BUYERS_PREMIUM_TYPE_LABELS = {
  Flat: 'Standard',
  Tiered: 'Tiered',
} as const;

/**
 * The minimum shape {@link buyersPremiumTypeLabel} needs.
 *
 * Typed on the fields the function reads rather than on `BuyersPremium`, per
 * ADR 010 / `sharing.md` § 3 rule 2 — so BOTH a canonical stored schedule and a
 * loose in-progress editor draft satisfy it. That matters: the editor's live
 * preview labels a draft that is legitimately incomplete mid-typing, and if it
 * couldn't call this it would go back to building the string itself, which is
 * the duplication BL-1375 exists to remove.
 *
 * `combineMode` is optional because a draft may not have chosen one yet.
 */
export type BuyersPremiumTypeLike =
  | { type: 'Flat' }
  | { type: 'Tiered'; combineMode?: BuyersPremiumCombineMode | null };

/**
 * Name a schedule's calculation type on its own — `"Standard"`,
 * `"Tiered (Marginal)"`, `"Tiered (Bracket)"`.
 *
 * The type label WITHOUT the rate/bounds that {@link formatBuyersPremium}
 * appends, for surfaces that render the type in its own labelled slot (the
 * tenant-settings summary card's "Type" row) or beside a rate they already show
 * (the editor's live preview row).
 *
 * Exists so those surfaces stop inventing their own word. The summary row used
 * to render `value.type` — the raw persisted enum, straight to an operator —
 * which is exactly why it never followed the editor's rename.
 *
 * A draft with no `combineMode` yet defaults to `Marginal`, matching the
 * calculator's own default, so a half-built tiered schedule never previews under
 * a mode it wouldn't be billed at.
 *
 * @param bp - Anything carrying a buyer's-premium `type` (stored schedule or
 *   editor draft) — see {@link BuyersPremiumTypeLike}.
 * @returns The type's operator-facing name.
 * @sideEffect None — pure.
 */
export function buyersPremiumTypeLabel(bp: BuyersPremiumTypeLike): string {
  if (bp.type === 'Flat') return BUYERS_PREMIUM_TYPE_LABELS.Flat;
  return `${BUYERS_PREMIUM_TYPE_LABELS.Tiered} (${bp.combineMode ?? 'Marginal'})`;
}

/**
 * Render a tiered schedule's rate bands, in billing order, truncating long
 * schedules to the first band plus a remaining count.
 */
function tierSegments(
  bp: Extract<DisplaySchedule, { type: 'Tiered' }>,
  currency: string | null | undefined,
): string[] {
  const sorted = sortedBillingTiers(bp);

  const band = (tier: DisplayTier, index: number): string => {
    const rate = tier.rate.toPercent();
    if (tier.threshold != null) return `${rate} to ${amountText(tier.threshold, currency)}`;
    return index === 0 ? rate : `${rate} above`;
  };

  if (sorted.length > MAX_TIERS_SHOWN) {
    return [band(sorted[0], 0), `+${sorted.length - 1} tiers`];
  }

  return sorted.map(band);
}

/**
 * Sort a tiered schedule's tiers into the order they are actually billed —
 * ascending by threshold, unbounded (`null`) last. Shared by the one-line
 * summary and the full band disclosure so both always describe a schedule in
 * billing order, even when the stored array is out of order.
 */
function sortedBillingTiers(bp: Extract<DisplaySchedule, { type: 'Tiered' }>): DisplayTier[] {
  return [...bp.tiers].sort((a, b) => {
    if (a.threshold == null) return b.threshold == null ? 0 : 1;
    if (b.threshold == null) return -1;
    return amountValue(a.threshold).comparedTo(amountValue(b.threshold));
  });
}

/**
 * Render one tier as a full-disclosure phrase.
 *
 * THE BUG-783 RULE lives here: a non-first band's copy references the
 * **previous** tier's threshold, never its own. The unbounded remainder tier's
 * own threshold is `null`, and the original hand-rolled walk in the bidder's
 * `TieredBreakdown` read it directly — `Math.abs(null) === 0` rendered the
 * notorious "above $0". Reading `tiers[i - 1]` is the fix, and keeping it in
 * one function is what stops it regressing in a second copy.
 *
 * @param tier - The tier to describe.
 * @param index - Its position in billing order.
 * @param previousThreshold - The preceding tier's upper bound (`null` for the
 *   first tier, which has no predecessor to sit above).
 * @param bracket - Bracket mode charges the WHOLE hammer at the single tier it
 *   lands in, so its copy must not imply a marginal slice.
 * @param amount - Prints a boundary amount (in the auction's currency, BUG-1909).
 */
function bandPhrase(
  tier: DisplayTier,
  index: number,
  previousThreshold: DisplayAmount | null,
  bracket: boolean,
  amount: (value: DisplayAmount) => string,
): string {
  const rate = tier.rate.toPercent();
  const scope = bracket ? ' on the whole amount' : '';
  if (index === 0) {
    return tier.threshold != null
      ? `${rate}${scope} up to ${amount(tier.threshold)}`
      : `${rate}${scope}`;
  }
  const over = bracket ? 'over' : 'above';
  return previousThreshold != null
    ? `${rate}${scope} ${over} ${amount(previousThreshold)}`
    : `${rate}${scope}`;
}

/**
 * Whether a resolved buyer's-premium config actually charges the buyer anything.
 *
 * `false` for BOTH "nothing configured" and "an explicit 0% waiver" — the two
 * are different operator decisions but the same fact for a buyer, and every
 * surface that says "No buyer's premium" needs exactly this question answered.
 *
 * A waiver can be spelled either way the editor allows — a 0% flat rate, or a
 * tiered schedule whose every band is 0% — and both answer this question the
 * same, because a buyer under either pays nothing (BUG-1031). Neither is a
 * waiver once a `minFee` is attached; see {@link chargesNothing}.
 *
 * Exists so the 0%-waiver rule is encoded ONCE. Re-checking `flatRate === 0`
 * inline at each call site is the `0`-is-falsy bug class waiting to happen: a
 * `!bp.flatRate` or `bp.flatRate ||` at any one of them silently turns a
 * deliberate waiver into "inherit the parent's rate" — and the tiered spelling
 * makes an inline re-check strictly worse, since it needs an `.every` walk.
 *
 * @param bp - A RESOLVED buyer's premium (post-cascade).
 * @sideEffect None — pure.
 */
export function hasBuyersPremium(bp: BuyersPremiumScheduleInput): boolean {
  const settings = toDisplay(bp);
  return settings != null && !chargesNothing(settings);
}

/**
 * Describe a buyer's-premium config as a list of full-disclosure band phrases,
 * in billing order — e.g. `["18% up to $5,000", "16% above $5,000"]`.
 *
 * The counterpart to {@link formatBuyersPremium}: that one is a
 * space-constrained single line which truncates long schedules to
 * `"…+N tiers"` and drops the remainder band's boundary; this one is the
 * COMPLETE disclosure and never truncates. Two surfaces consume it — the
 * bidder bid panel's tiered info icon (joined with `" · "`) and the buyer's-
 * premium details modal's band list (one entry per row).
 *
 * Like the formatter, it deliberately never blends tiers into an "effective
 * rate": a blended "16.4% on this bid" is a number that appears on no document
 * the buyer ever receives.
 *
 * @param bp - A value read from any BP column.
 * @param options.currency - The auction's currency; every amount is written in
 *   it (BUG-1909). See {@link ScheduleCurrencyOption}.
 * @returns The band phrases, `["No buyer's premium"]` for a schedule that
 *   waives the premium (a 0% flat rate, or all-zero tiered bands — see
 *   {@link hasBuyersPremium}), or an empty array when nothing usable is
 *   configured. The empty case mirrors the formatter's empty string — "unset" is
 *   not an operator decision, so the CALLER owns what to say about it.
 * @sideEffect None — pure.
 */
export function describeBuyersPremiumBands(
  bp: BuyersPremiumScheduleInput,
  options: ScheduleCurrencyOption,
): string[] {
  const { currency } = options;
  return bandsOf(toDisplay(bp), (value) => amountText(value, currency));
}

/** The full band list for a normalized schedule, amounts printed by `amount`. */
function bandsOf(settings: DisplaySchedule | null, amount: (value: DisplayAmount) => string): string[] {
  if (settings == null) return [];

  if (chargesNothing(settings)) return [NO_PREMIUM];

  const bands: string[] = [];
  if (settings.type === 'Flat') {
    bands.push(`${settings.rate.toPercent()} on the whole amount`);
  } else {
    const tiers = sortedBillingTiers(settings);
    const bracket = settings.combineMode === 'Bracket';
    tiers.forEach((tier, i) => {
      bands.push(bandPhrase(tier, i, i === 0 ? null : tiers[i - 1].threshold, bracket, amount));
    });
  }

  if (settings.minFee != null) bands.push(`${amount(settings.minFee)} minimum`);
  if (settings.maxFee != null) bands.push(`${amount(settings.maxFee)} maximum`);

  return bands;
}

/**
 * Format a buyer's-premium config as a one-line human-readable summary.
 *
 * Consumes a {@link PremiumSchedule} or the canonical stored union (BL-2482). Before BL-990's
 * reset migration this normalized on entry, so that a row storing its ceiling as
 * the retired `flatCap` still showed a cap; the migration folded every such row
 * into `maxFee` on disk, so there is nothing left to repair here.
 *
 * @param bp - A value read from any BP column.
 * @param options - See {@link FormatBuyersPremiumOptions}; `currency` is the
 *   auction's, and every amount is written in it (BUG-1909).
 * @returns The summary, `"No buyer's premium"` for a schedule that waives the
 *   premium (a 0% flat rate, or all-zero tiered bands), or an empty string when
 *   nothing usable is configured. The empty case is
 *   deliberate: "unset" is not an operator decision, so the CALLER owns what to
 *   say about it (a lot view reads "No buyer's premium"; an auction card that
 *   inherits shows the house default instead).
 * @sideEffect None — pure.
 */
export function formatBuyersPremium(
  bp: BuyersPremiumScheduleInput,
  options: FormatBuyersPremiumOptions,
): string {
  const settings = toDisplay(bp);
  if (settings == null) return '';

  if (chargesNothing(settings)) return NO_PREMIUM;

  const segments: string[] = [];
  if (!options.compact) segments.push(buyersPremiumTypeLabel(settings));

  if (settings.type === 'Flat') {
    segments.push(settings.rate.toPercent());
  } else {
    segments.push(...tierSegments(settings, options.currency));
  }

  if (settings.minFee != null) segments.push(`${amountText(settings.minFee, options.currency)} min`);
  if (settings.maxFee != null) segments.push(`${amountText(settings.maxFee, options.currency)} cap`);

  return segments.join(SEP);
}

/**
 * The terms a buyer reads, as a key free of representation and currency: the
 * full band list ({@link describeBuyersPremiumBands}) with every amount written
 * as its plain exact value. Equal for the same terms whether the schedule
 * arrived as a PremiumSchedule (Money, which prints its own currency sign) or as
 * stored numbers (which print the caller's) — so a Buyers Guide mixing a decoded
 * auction rung with stored ring rungs still votes on the terms (BL-2482). `'∅'`
 * for a schedule that charges nothing (absent, or a waiver).
 *
 * @param bp - Either representation, or nothing.
 * @returns The key.
 * @sideEffect None — pure.
 */
export function buyersPremiumTermsKey(bp: BuyersPremiumScheduleInput): string {
  if (!hasBuyersPremium(bp)) return '\u2205';
  return JSON.stringify(bandsOf(toDisplay(bp), (value) => amountValue(value).toString()));
}
